import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import {
  SOURCES,
  recommend,
  fallbackColours,
  swatchHex,
  FALLBACK_UNIT,
  same,
  colourIndex,
  resolvePack,
  variantImageFor,
} from '@theme/lc-watering-logic';

/**
 * <lc-watering-configurator>
 *
 * Three-step "Build your watering setup" flow: water source → flock size → colour, with a live
 * summary card, optional add-ons and real add-to-cart.
 *
 * Catalogue model (matches the Lil'Clucker store): one product per colour, each with a
 * "Pack Size" option (5 / 8 / 12 pack). Container setups use the cups product for the colour;
 * PVC setups use the "Cups & Tees" product for the colour. Colours are theme-editor blocks.
 *
 * Progressive enhancement: the server renders the intro copy and a link for no-JS visitors.
 */

const ICONS = {
  check: '<path d="M20 6 9 17l-5-5"/>',
  droplet:
    '<path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z"/>',
  wrench:
    '<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>',
  'rotate-ccw': '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>',
};

/** @param {unknown} value */
const esc = (value) =>
  String(value ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c
  );

/** @param {string} name @param {number} size @param {string} color */
const icon = (name, size = 20, color = 'currentColor') =>
  `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="flex:0 0 auto;display:block">${ICONS[name] ?? ''}</svg>`;

/**
 * @typedef {import('@theme/lc-watering-logic').Variant} Variant
 * @typedef {import('@theme/lc-watering-logic').Product} Product
 * @typedef {{ name: string, hex: string, cups: Product | null, tees: Product | null }} ColourEntry
 * @typedef {{ name: string, hex: string, product: Product | null }} AddonColourEntry
 * @typedef {{ key: string, title: string, benefit: string, product: Product | null, colourOptions?: AddonColourEntry[], image: string, comingSoon: boolean, comingSoonLabel?: string, notifyUrl: string }} Addon
 */

class LcWateringConfigurator extends HTMLElement {
  /** @type {{ colours: ColourEntry[], addons: Addon[], previewImage: string, moneyFormat: string, currency: string, text: Record<string, string> }} */
  config = { colours: [], addons: [], previewImage: '', moneyFormat: '${{amount}}', currency: 'USD', text: {} };

  state = {
    source: /** @type {string | null} */ (null),
    flock: /** @type {number | null} */ (null),
    colour: /** @type {string | null} */ (null),
    /** How many of the recommended pack to add (design: "Why not grab an extra pack?"). */
    packQty: 1,
    edit: /** @type {string | null} */ (null),
    detailsOpen: false,
    expanded: /** @type {string | null} */ (null),
    /**
     * Per add-on: chosen option values, whether it is part of the setup (design: "Added · Gray · 4 pack"),
     * quantity, and the variant id the setup button last put in the cart for it.
     * @type {Record<string, { selected: Record<string, string>, chosen: boolean, qty: number, inCart: number | null }>}
     */
    addons: {},
    setupSignature: /** @type {string | null} */ (null),
    /** Variant ids the setup button last added (cups plus any chosen add-ons). */
    /** @type {number[]} */
    setupVariantIds: [],
    /** The cups (or cups & tees) variant id within setupVariantIds. */
    setupCupsVariantId: /** @type {number | null} */ (null),
    busy: false,
    cartCount: 0,
    cartTotal: 0,
    error: '',
  };

  #media = window.matchMedia('(max-width: 989px)');

  connectedCallback() {
    const script = this.querySelector('script[data-lc-config]');
    if (script?.textContent) {
      try {
        this.config = { ...this.config, ...JSON.parse(script.textContent) };
      } catch (error) {
        console.warn('[lc-watering-configurator] invalid config', error);
      }
    }

    this.addEventListener('click', this.#onClick);
    this.#media.addEventListener('change', this.#onMediaChange);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onExternalCartUpdate);
    // Cart changes that raise no theme event in this document (checkout completed then Back,
    // the cart page or Shop app in another tab): re-read the cart when the page is restored from
    // the back-forward cache or the tab becomes visible again, as Horizon's cart icon does.
    window.addEventListener('pageshow', this.#onPageShow);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    this.setAttribute('data-ready', 'true');
    this.render();
    this.#refreshCart().catch(() => {});
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#media.removeEventListener('change', this.#onMediaChange);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onExternalCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  #onMediaChange = () => this.render();

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#resyncFromCart();
  };

  #onVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.#resyncFromCart();
  };

  /** Re-read the Shopify cart and reconcile the panel's "added" state with it. */
  #resyncFromCart = () =>
    this.#refreshCart()
      .then((cart) => this.#syncFromCart(cart))
      .catch(() => {});

  /**
   * Cart changed somewhere else (cart drawer quantity/remove, a PDP, the drawer's recommendations):
   * once the update settles, re-read the cart and drop anything this panel still counts as added
   * but that is no longer in the cart (design: removing a line clears the add-on / resets the setup CTA).
   * @param {Event} event
   */
  #onExternalCartUpdate = (event) => {
    if (event.target === this || (event.target instanceof Node && this.contains(event.target))) return;
    const promise = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
    // Sync once the update settles either way: a failed drawer request can still have left the
    // cart in a state this panel has not seen.
    if (promise) promise.then(this.#resyncFromCart, this.#resyncFromCart);
    else this.#resyncFromCart();
  };

  /** @param {{ items?: { variant_id: number, quantity: number }[] }} cart */
  #syncFromCart(cart) {
    const inCart = new Map();
    for (const item of cart.items ?? []) inCart.set(item.variant_id, (inCart.get(item.variant_id) ?? 0) + item.quantity);

    let changed = false;
    // An add-on removed from the cart drops out of the setup (design: cartRemove clears the add-on);
    // a quantity changed in the cart is mirrored on the card.
    for (const st of Object.values(this.state.addons)) {
      if (!st.inCart) continue;
      const qty = inCart.get(st.inCart);
      if (!qty) {
        st.inCart = null;
        st.chosen = false;
        st.selected = {};
        st.qty = 1;
        changed = true;
      } else if (qty !== st.qty) {
        st.qty = Math.min(20, qty);
        changed = true;
      }
    }
    // The cups line removed from the cart: the setup is no longer "added" (the button offers it again).
    if (this.state.setupCupsVariantId && !inCart.has(this.state.setupCupsVariantId)) {
      this.state.setupSignature = null;
      this.state.setupVariantIds = [];
      this.state.setupCupsVariantId = null;
      changed = true;
    } else if (changed && this.state.setupSignature) {
      // Still in the cart with adjusted add-ons: keep the button in its "added" state.
      this.state.setupVariantIds = this.state.setupVariantIds.filter((id) => inCart.has(id));
      this.state.setupSignature = this.setup().signature;
    }
    if (changed) this.render();
  }

  get isMobile() {
    return this.#media.matches;
  }

  get hasProducts() {
    return this.config.colours.some((c) => c.cups || c.tees);
  }

  /** @param {number} cents */
  money(cents) {
    return formatMoney(Math.round(cents), this.config.moneyFormat, this.config.currency);
  }

  /** @param {Partial<typeof this.state>} patch */
  setState(patch) {
    Object.assign(this.state, patch);
    this.render();
  }

  // ------------------------------------------------------------------ derived data

  /** Colour entries that have a product for the chosen source. */
  colourEntries() {
    const { source } = this.state;
    if (!source) return [];
    const withProduct = this.config.colours.filter((c) => (source === 'pvc' ? c.tees : c.cups));
    if (withProduct.length) return withProduct;
    return fallbackColours(source).map((name) => ({ name, hex: swatchHex(name), cups: null, tees: null }));
  }

  /** @param {string | null} colour */
  entryFor(colour) {
    if (!colour) return null;
    return this.colourEntries().find((c) => same(c.name, colour)) ?? null;
  }

  /** @param {ColourEntry | null} entry */
  productFor(entry) {
    if (!entry) return null;
    return this.state.source === 'pvc' ? entry.tees : entry.cups;
  }

  /** Whether `entry` has a purchasable variant for the given cup count (no product yet = preview, treated as available). */
  /** @param {ColourEntry} entry @param {number} cups */
  colourAvailable(entry, cups) {
    if (!cups) return true;
    const product = this.productFor(entry);
    if (!product) return true;
    const pack = resolvePack(product, entry.name, cups);
    return pack ? pack.variant.available !== false : true;
  }

  /** @param {string | null} colour */
  hexFor(colour) {
    const entry = this.entryFor(colour);
    return entry?.hex || swatchHex(colour ?? '');
  }

  /** @param {string | null} colour */
  imageFor(colour) {
    const entry = this.entryFor(colour);
    const product = this.productFor(entry);
    if (!product || !colour) return '';
    return variantImageFor(product, colour);
  }

  /**
   * Build the current setup: recommendation, resolved lines and total.
   *
   * Design (Homepage, Sept 2026): the summary lists the cups product line plus every add-on
   * chosen for the setup, each with a Remove link, and one "Order total". The setup button adds
   * the cups and the chosen add-ons to the cart together.
   */
  setup() {
    const { source, flock, colour } = this.state;
    const rec = recommend(source, flock);
    const cups = rec.cups;
    const packs = Math.max(1, this.state.packQty || 1);
    const complete = Boolean(rec.src && rec.band && colour);
    const t = this.config.text;

    /** @type {{ key: string, name: string, price: number, priceLabel: string, variantId: number | null, quantity: number }[]} */
    const lines = [];
    let total = 0;
    let purchasable = true;
    /** @type {string[]} */
    const addonSig = [];

    if (rec.band) {
      // Price from the chosen colour's product, or the first colour's product as an estimate.
      const entry = this.entryFor(colour) ?? this.colourEntries()[0] ?? null;
      const product = this.productFor(entry);
      const pack = resolvePack(product, colour, cups);
      const isPvc = rec.src === 'pvc';
      const key = isPvc ? 'tees' : 'cups';
      const title = isPvc ? t.teesLineTitle || 'Automatic Waterer Cups & PVC Tees' : t.cupsLineTitle || 'Automatic Waterer Cups';
      const lineName = `${packs > 1 ? `${packs} × ` : ''}${title} (${[colour, `${cups} pack`].filter(Boolean).join(', ')})`;

      if (pack) {
        const quantity = pack.quantity * packs;
        const price = pack.variant.price * quantity;
        lines.push({ key, name: lineName, price, priceLabel: this.money(price), variantId: colour ? pack.variant.id : null, quantity });
        total += price;
        if (!pack.variant.available) purchasable = false;
      } else {
        const unit = isPvc ? FALLBACK_UNIT.cup + FALLBACK_UNIT.tee : FALLBACK_UNIT.cup;
        const price = cups * packs * unit;
        lines.push({ key, name: lineName, price, priceLabel: this.money(price), variantId: null, quantity: packs });
        total += price;
        purchasable = false;
      }

      for (const addon of this.addonViews()) {
        if (!addon.added) continue;
        if (!addon.resolved) {
          purchasable = false;
          continue;
        }
        const linePrice = addon.resolved.price * addon.qty;
        lines.push({ key: `addon-${addon.key}`, name: (addon.qty > 1 ? `${addon.qty} × ` : '') + addon.label, price: linePrice, priceLabel: this.money(linePrice), variantId: addon.resolved.id, quantity: addon.qty });
        total += linePrice;
        if (addon.resolved.available === false) purchasable = false;
        addonSig.push(`${addon.key}:${addon.resolved.id}:${addon.qty}`);
      }
    }

    const signature = complete ? [rec.src, flock, colour, packs, ...addonSig.sort()].join('|') : null;
    const setupInCart = Boolean(signature) && this.state.setupSignature === signature;

    return { ...rec, colour, packs, complete, lines: complete ? lines : [], total, signature, setupInCart, purchasable: purchasable && this.hasProducts };
  }

  /**
   * Per-addon view model.
   *
   * Most add-ons are one Shopify product whose own variant options (e.g. a "Colour" option)
   * drive the chip rows below. A few add-ons — nesting pads being the case in this catalogue —
   * are sold as separate colour-specific products rather than colour variants of one product.
   * For those, `addon.colourOptions` supplies the colour → product map and a synthetic "Colour"
   * chip row is prepended so the shopper still sees one card with one set of choices, not one
   * card per colour.
   */
  addonViews() {
    return this.config.addons.map((addon) => {
      const st = this.state.addons[addon.key] ?? { selected: {}, chosen: false, qty: 1, inCart: null };
      const qty = Math.max(1, st.qty || 1);
      const colourOptions = addon.colourOptions || [];
      const isMultiColour = colourOptions.length > 0;
      const selectedColourEntry = isMultiColour ? colourOptions.find((c) => same(c.name, st.selected.Colour)) : null;
      // Before a colour is chosen, use the first purchasable colour's product as a template so the
      // other variant options (e.g. Pack Size) can already be shown — every colour shares the set.
      // A colour entry without a product (unpublished from the Online Store) is listed as sold out.
      const product = isMultiColour
        ? selectedColourEntry?.product ?? colourOptions.find((c) => c.product)?.product ?? null
        : addon.product;

      // Single-product add-ons keep every option (a chick kit's own Colour option included);
      // colour-per-product add-ons drop any product-level colour option in favour of the
      // synthetic colour row. Variants are matched by each option's real position.
      const allOptions = product && !product.hasOnlyDefaultVariant ? product.options : [];
      const productOptions = isMultiColour ? allOptions.filter((o) => !/colou?r/i.test(o.name)) : allOptions;
      const options = isMultiColour
        ? [{ name: 'Colour', position: 0, values: colourOptions.map((c) => c.name) }, ...productOptions]
        : productOptions;

      // Values that cannot be bought right now: a colour whose product is missing or has no
      // available variant, or an option value with no available variant. Rendered disabled.
      /** @type {Record<string, string[]>} */
      const unavailable = {};
      if (isMultiColour) {
        unavailable.Colour = colourOptions.filter((c) => !c.product || !c.product.variants.some((v) => v.available)).map((c) => c.name);
      }
      if (product) {
        for (const o of allOptions) {
          const pos = (o.position || allOptions.indexOf(o) + 1) - 1;
          const dead = o.values.filter((v) => !product.variants.some((x) => same(x.options[pos], v) && x.available));
          if (dead.length) unavailable[o.name] = dead;
        }
      }
      const isUnavailable = (/** @type {string} */ name, /** @type {string} */ value) => (unavailable[name] ?? []).some((v) => same(v, value));

      const ready = (!isMultiColour || Boolean(selectedColourEntry?.product)) && productOptions.every((o) => st.selected[o.name]);
      const resolved = product
        ? productOptions.length
          ? product.variants.find((v) => productOptions.every((o, i) => same(v.options[(o.position || i + 1) - 1], st.selected[o.name])))
          : product.variants[0]
        : null;
      const minPrice = product?.variants.length ? Math.min(...product.variants.map((v) => v.price)) : 0;
      const chosenBits = [selectedColourEntry?.name, ...productOptions.map((o) => st.selected[o.name])].filter(Boolean);
      const ci = isMultiColour ? -1 : colourIndex(product);
      const colourName = isMultiColour ? selectedColourEntry?.name ?? '' : ci >= 0 ? st.selected[product?.options[ci]?.name ?? ''] : '';
      const image = (resolved?.image || (colourName ? variantImageFor(product, colourName) : '') || product?.image || addon.image) ?? '';
      // A connected product with no variant in stock gets the same non-purchasable treatment as a
      // not-yet-launched add-on (sold-out label + Notify me), and becomes a normal add-on again
      // as soon as Shopify reports stock. Without a product the editor's coming-soon flag applies.
      const soldOut = Boolean(product) && !product.variants.some((v) => v.available);
      const comingSoon = soldOut || (!product && Boolean(addon.comingSoon));

      return {
        key: addon.key,
        title: addon.title,
        benefit: addon.benefit,
        product,
        options,
        selected: st.selected,
        qty,
        chosenBits,
        ready: ready && Boolean(resolved) && resolved.available !== false,
        resolved: resolved ?? null,
        isUnavailable,
        added: Boolean(st.chosen),
        open: this.state.expanded === addon.key,
        image,
        label: addon.title + (chosenBits.length ? ` (${chosenBits.join(', ')})` : ''),
        priceLabel: comingSoon
          ? addon.comingSoonLabel || this.config.text.comingSoon || 'Price coming soon'
          : resolved
            ? this.money(resolved.price * qty)
            : product
              ? `From ${this.money(minPrice)}`
              : '',
        needsChoice: options.length > 0,
        comingSoon,
        notifyUrl: addon.notifyUrl || '',
      };
    });
  }

  // ------------------------------------------------------------------ rendering

  render() {
    const steps = this.querySelector('[data-lc-steps]');
    const summary = this.querySelector('[data-lc-summary]');
    const addons = this.querySelector('[data-lc-addons]');
    const cartNote = this.querySelector('[data-lc-cart-note]');
    const mark = this.querySelector('[data-lc-mark]');
    const setup = this.setup();

    // The chick mark heads the panel until the first question is answered (design: isDefault).
    if (mark instanceof HTMLElement) mark.hidden = Boolean(this.state.source);
    if (steps) steps.innerHTML = this.renderSteps(setup);
    if (summary) summary.innerHTML = this.renderSummary(setup);
    if (addons) {
      const show = setup.complete && this.config.addons.length > 0;
      addons.hidden = !show;
      addons.innerHTML = show ? this.renderAddons() : '';
    }
    if (cartNote) {
      const show = this.isMobile && this.state.cartCount > 0;
      cartNote.hidden = !show;
      cartNote.innerHTML = show ? this.renderCartNote() : '';
    }
  }

  /** @param {ReturnType<LcWateringConfigurator['setup']>} setup */
  renderSteps(setup) {
    const t = this.config.text;
    const mobile = this.isMobile;
    const { source, colour, edit } = this.state;
    const hasBand = Boolean(setup.band);

    const s1Open = !mobile || !source || edit === 'source';
    const s2Open = Boolean(source) && (!mobile || (!s1Open && (!hasBand || edit === 'flock')));
    const s3Open = hasBand && (!mobile || (!s1Open && !s2Open && (!colour || edit === 'colour')));

    const parts = [];

    if (s1Open) {
      parts.push(`
        <div class="lc-config__step">
          <div class="lc-config__step-label">${esc(t.step1 || '1 · How will you supply the water?')}</div>
          <div class="lc-config__sources">
            ${SOURCES.map(
              (s) => `
              <button type="button" class="lc-choice lc-config__source" data-action="source" data-value="${s.key}" aria-pressed="${source === s.key}">
                <span class="lc-config__source-head">
                  <span class="lc-radio-dot" aria-hidden="true"></span>
                  ${icon(s.icon, 22, '#3A7DB8')}
                </span>
                <span class="lc-config__source-text">
                  <span class="lc-config__option-label">${esc(s.label)}</span>
                  <span class="lc-config__option-sub">${esc(s.sub)}</span>
                </span>
              </button>`
            ).join('')}
          </div>
        </div>`);
    } else if (mobile && source) {
      parts.push(this.renderDoneRow(t.sourceLabel || 'Water source', source === 'pvc' ? 'PVC pipe' : 'Container / bucket', 'source'));
    }

    if (s2Open) {
      parts.push(`
        <div class="lc-config__step">
          <div class="lc-config__step-label">${esc(t.step2 || '2 · How many chickens?')}</div>
          <div class="lc-config__flock">
            ${setup.bands
              .map(
                (b, i) => `
              <button type="button" class="lc-choice lc-config__flock-option" data-action="flock" data-value="${i}" aria-pressed="${this.state.flock === i}">
                <span class="lc-config__option-label">${esc(b.label)}</span>
                <span class="lc-config__option-sub">${b.cups} cups</span>
              </button>`
              )
              .join('')}
          </div>
          ${
            hasBand && !mobile
              ? `<div class="lc-config__recommend">${icon('check', 18, '#058346')}<span>${esc((t.recommended || 'Recommended for your flock: [cups] watering cups').replace('[cups]', String(setup.cups)))}</span></div>`
              : ''
          }
        </div>`);
    } else if (mobile && source && hasBand && !s1Open) {
      parts.push(this.renderDoneRow(t.flockLabel || 'Flock size', `${setup.band?.label} · ${setup.cups} cups`, 'flock'));
    }

    if (s3Open) {
      const entries = this.colourEntries();
      parts.push(`
        <div class="lc-config__step">
          <div class="lc-config__step-label">${esc(t.step3 || '3 · Choose your colour')}</div>
          <div class="lc-config__colours" style="--lc-colour-count:${entries.length}">
            ${entries
              .map((entry) => {
                const img = this.imageFor(entry.name) || this.config.previewImage;
                const available = this.colourAvailable(entry, setup.cups);
                return `
              <button type="button" class="lc-choice lc-config__colour${available ? '' : ' lc-config__colour--unavailable'}" data-action="colour" data-value="${esc(entry.name)}" aria-pressed="${same(colour, entry.name)}" ${available ? '' : 'disabled aria-disabled="true"'}>
                ${img ? `<img src="${esc(img)}" alt="" loading="lazy" class="lc-config__colour-img" width="400" height="400">` : '<span class="lc-config__colour-img lc-config__colour-img--empty"></span>'}
                <span class="lc-config__colour-name">
                  <span class="lc-swatch" style="background:${esc(entry.hex || swatchHex(entry.name))}"></span>
                  <span>${esc(entry.name)}</span>
                </span>
                ${available ? '' : `<span class="lc-config__colour-soldout">${esc(t.soldOut || 'Sold out')}</span>`}
              </button>`;
              })
              .join('')}
          </div>
          ${this.renderExtraPack(setup)}
        </div>`);
    } else if (mobile && colour && hasBand && !s1Open && !s2Open) {
      parts.push(this.renderDoneRow(t.colourLabel || 'Colour', colour, 'colour'));
    }

    return parts.join('');
  }

  /** "Why not grab an extra pack?" — a quantity stepper for the recommended pack. */
  /** @param {ReturnType<LcWateringConfigurator['setup']>} setup */
  renderExtraPack(setup) {
    const t = this.config.text;
    if (!setup.band) return '';
    const packs = setup.packs;
    const note =
      packs === 1
        ? (t.packNoteOne || '[cups]-cup pack').replace('[cups]', String(setup.cups))
        : (t.packNoteMany || '[packs] packs · [cups] cups').replace('[packs]', String(packs)).replace('[cups]', String(setup.cups * packs));
    return `
      <div class="lc-config__extra">
        <span class="lc-config__extra-label">${esc(t.extraPack || 'Why not grab an extra pack?')}</span>
        <span class="lc-config__stepper" role="group" aria-label="${esc(t.extraPack || 'Packs')}">
          <button type="button" data-action="pack-qty" data-value="-1" aria-label="Fewer packs" ${packs <= 1 ? 'disabled' : ''}>−</button>
          <span class="lc-config__stepper-value">${packs}</span>
          <button type="button" data-action="pack-qty" data-value="1" aria-label="More packs" ${packs >= 10 ? 'disabled' : ''}>+</button>
        </span>
        <span class="lc-config__extra-note">${esc(note)}</span>
      </div>`;
  }

  /** @param {string} label @param {string} value @param {string} step */
  renderDoneRow(label, value, step) {
    return `
      <div class="lc-config__done">
        ${icon('check', 17, '#FFCC46')}
        <span class="lc-config__done-text">
          <span class="lc-config__done-label">${esc(label)}</span>
          <span class="lc-config__done-value">${esc(value)}</span>
        </span>
        <button type="button" class="lc-config__edit" data-action="edit" data-value="${step}">${esc(this.config.text.edit || 'Edit')}</button>
      </div>`;
  }

  /** @param {ReturnType<LcWateringConfigurator['setup']>} setup */
  renderSummary(setup) {
    const t = this.config.text;
    const mobile = this.isMobile;
    const { colour } = this.state;
    const hasSource = Boolean(setup.src);
    const image = (colour ? this.imageFor(colour) : '') || this.config.previewImage;
    // Design: "8 × Orange Automatic Water Cups (Container)"; before a colour, the colour is left out;
    // before a flock size, "Container install" / "PVC install".
    const kind = setup.src === 'pvc' ? t.pvcKind || 'PVC' : t.containerKind || 'Container';
    const totalCups = setup.cups * setup.packs;

    let title;
    let hint;
    if (setup.band) {
      title = `${totalCups} × ${setup.complete ? `${colour} ` : ''}${t.summaryCups || 'Automatic Water Cups'} (${kind})`;
      hint = setup.complete ? '' : t.hintColour || 'Choose a colour to finish.';
    } else {
      title = setup.src === 'pvc' ? t.pvcTitle || 'PVC install' : t.containerTitle || 'Container install';
      hint = t.hintFlock || 'Tell us how many chickens you water.';
    }

    const contents = [
      setup.band ? `${setup.cups} cups + ${setup.src === 'pvc' ? 'required tee fittings' : 'sealing washers'}` : '',
      hint,
    ]
      .filter(Boolean)
      .join(' · ');

    // Each line carries a Remove link (design): the cups line clears the flock and colour answers,
    // an add-on line drops that add-on from the setup.
    const linesHtml = setup.lines
      .map(
        (l) => `<div class="lc-config__line"><span class="lc-config__line-name">${esc(l.name)} <button type="button" class="lc-config__line-remove" data-action="line-remove" data-key="${esc(l.key)}" ${this.state.busy ? 'disabled' : ''}>${esc(t.remove || 'Remove')}</button></span><span class="lc-config__line-price">${esc(l.priceLabel)}</span></div>`
      )
      .join('');

    // Once added, the desktop button becomes "Setup added — view cart" and opens the cart drawer
    // (design); on mobile it reads "✓ Setup added" and the "Ready to go?" callout offers View cart.
    const added = Boolean(setup.signature) && this.state.setupSignature === setup.signature;
    const viewCart = added && !this.isMobile;
    const ctaLabel = added
      ? this.isMobile
        ? t.setupAddedMobile || '✓ Setup added'
        : t.setupAdded || 'Setup added — view cart'
      : t.addSetup || 'Add setup to cart';
    const ctaDisabled = viewCart ? this.state.busy : added || !setup.purchasable || this.state.busy;

    const totals = setup.complete
      ? `
        <div class="lc-config__totals">
          <div class="lc-config__total-row">
            <span class="lc-config__total-label">${esc(t.total || 'Order total')}</span>
            <span class="lc-config__total-values">
              <span class="lc-config__total">${this.money(setup.total)}</span>
            </span>
          </div>
          ${!setup.purchasable && this.hasProducts ? `<div class="lc-config__error">${esc(t.unavailable || 'Part of this setup is currently unavailable.')}</div>` : ''}
          ${!this.hasProducts ? `<div class="lc-config__error">${esc(t.noProducts || 'Connect the cups products in the theme editor to enable checkout.')}</div>` : ''}
          ${this.state.error ? `<div class="lc-config__error">${esc(this.state.error)}</div>` : ''}
          <button type="button" class="lc-btn lc-btn--primary lc-btn--lg lc-btn--full lc-btn--square lc-config__cta" data-action="${viewCart ? 'view-cart' : 'add-setup'}" ${ctaDisabled ? 'disabled' : ''}>${esc(ctaLabel)}</button>
          ${t.guarantee ? `<div class="lc-config__guarantee">${icon('rotate-ccw', 16)}<span>${esc(t.guarantee)}</span></div>` : ''}
        </div>`
      : '';

    if (!hasSource) {
      return `
        <div class="lc-config__summary-label">${esc(t.summaryLabel || 'Your setup')}</div>
        <div class="lc-config__summary-intro">
          ${image ? `<img class="lc-config__summary-img" src="${esc(image)}" alt="" loading="lazy" width="800" height="600">` : '<span class="lc-config__summary-img lc-config__summary-img--empty"></span>'}
          <div>
            <div class="lc-config__summary-title">${esc(t.introTitle || "We'll build the right setup for your flock")}</div>
            <p class="lc-config__summary-hint">${esc(mobile ? t.introTextMobile || 'Answer the three questions above.' : t.introText || "Choose your water source, flock size and colour. We'll work out the right cup quantity for you.")}</p>
          </div>
        </div>
        ${
          mobile
            ? ''
            : `<div class="lc-config__intro-steps">
            ${['Water source', 'Flock size', 'Colour'].map((label, i) => `<div class="lc-config__intro-step"><span class="lc-config__intro-num">${i + 1}</span><span>${esc(label)}</span></div>`).join('')}
          </div>`
        }`;
    }

    return `
      <div class="lc-config__summary-label">${esc(t.summaryLabel || 'Your setup')}</div>
      <div class="lc-config__summary-main">
        ${image ? `<img class="lc-config__summary-img" src="${esc(image)}" alt="" loading="lazy" width="800" height="800">` : '<span class="lc-config__summary-img lc-config__summary-img--empty"></span>'}
        <div class="lc-config__summary-text">
          <div class="lc-config__summary-title">${esc(title)}</div>
          <div class="lc-config__summary-hint">${esc(mobile ? contents : hint)}</div>
        </div>
      </div>
      ${setup.lines.length && !mobile ? `<div class="lc-config__lines">${linesHtml}</div>` : ''}
      ${totals}
      ${
        mobile && setup.lines.length && setup.complete
          ? `<button type="button" class="lc-config__details-toggle" data-action="toggle-details">${esc(this.state.detailsOpen ? t.hideDetails || 'Hide setup details' : t.viewDetails || 'View setup details')}</button>
             ${this.state.detailsOpen ? `<div class="lc-config__lines">${linesHtml}</div>` : ''}`
          : ''
      }`;
  }

  renderAddons() {
    const t = this.config.text;
    const views = this.addonViews();
    if (!views.length) return '';

    const cards = views
      .map((a) => {
        const head = `
          <div class="lc-config__addon-head">
            ${a.image ? `<img class="lc-config__addon-img" src="${esc(a.image)}" alt="" loading="lazy" width="88" height="88">` : '<span class="lc-config__addon-img lc-config__addon-img--empty"></span>'}
            <div class="lc-config__addon-text">
              <div class="lc-config__addon-name">${esc(a.title)}</div>
              ${a.benefit ? `<div class="lc-config__addon-benefit">${esc(a.benefit)}</div>` : ''}
              <div class="lc-config__addon-price lc-config__addon-price--mobile">${esc(a.priceLabel)}</div>
            </div>
            ${
              a.added
                ? `<div class="lc-config__addon-added">
                     <span class="lc-config__addon-added-label">${icon('check', 15, '#058346')}${esc([(t.added || 'Added') + (a.qty > 1 ? ` ×${a.qty}` : ''), ...a.chosenBits].join(' · '))}</span>
                     <span class="lc-config__addon-added-actions">
                       ${a.needsChoice ? `<button type="button" class="lc-config__text-btn" data-action="addon-edit" data-key="${esc(a.key)}">${esc(t.edit || 'Edit')}</button>` : ''}
                       <button type="button" class="lc-config__text-btn lc-config__text-btn--muted" data-action="addon-remove" data-key="${esc(a.key)}">${esc(t.remove || 'Remove')}</button>
                     </span>
                   </div>`
                : a.open
                  ? ''
                  : a.comingSoon
                    ? `<div class="lc-config__addon-cta">
                         <span class="lc-config__addon-price lc-config__addon-price--desktop">${esc(a.priceLabel)}</span>
                         <a class="lc-btn lc-btn--outline lc-btn--sm lc-config__addon-btn" href="${esc(a.notifyUrl || '#')}">${esc(t.notifyMe || 'Notify me')}</a>
                       </div>`
                    : `<div class="lc-config__addon-cta">
                       <span class="lc-config__addon-price lc-config__addon-price--desktop">${esc(a.priceLabel)}</span>
                       <button type="button" class="lc-btn lc-btn--primary lc-btn--sm lc-config__addon-btn" data-action="addon-add" data-key="${esc(a.key)}" ${!a.product || this.state.busy ? 'disabled' : ''}>${esc(a.needsChoice ? t.choose || 'Choose' : t.add || 'Add')}</button>
                     </div>`
            }
          </div>`;

        // Open panel: size-type options as a segmented control, colour options as swatch circles,
        // a quantity stepper, then the confirm row (design: homepage add-on card).
        const panel = a.open
          ? `<div class="lc-config__addon-panel">
               <div class="lc-config__addon-controls">
                 <div class="lc-config__addon-options">
                   ${a.options
                     .map((o) => {
                       const isColour = /colou?r/i.test(o.name);
                       const soldOut = t.soldOut || 'Sold out';
                       const control = isColour
                         ? `<span class="lc-config__swatches">
                              ${o.values
                                .map((v) => {
                                  const dead = a.isUnavailable(o.name, v);
                                  const label = dead ? `${v} · ${soldOut}` : v;
                                  return `<button type="button" class="lc-config__swatch${dead ? ' lc-config__swatch--unavailable' : ''}" data-action="addon-pick" data-key="${esc(a.key)}" data-option="${esc(o.name)}" data-value="${esc(v)}" aria-pressed="${same(a.selected[o.name], v)}" aria-label="${esc(label)}" title="${esc(label)}" style="background:${swatchHex(v)}" ${dead ? 'disabled aria-disabled="true"' : ''}></button>`;
                                })
                                .join('')}
                              <span class="lc-config__swatch-name">${esc(a.selected[o.name] || t.chooseColour || 'Choose a colour')}</span>
                              ${o.values.some((v) => a.isUnavailable(o.name, v)) ? `<span class="lc-config__swatch-soldout">(${esc(o.values.filter((v) => a.isUnavailable(o.name, v)).join(', '))} ${esc(soldOut.toLowerCase())})</span>` : ''}
                            </span>`
                         : `<span class="lc-config__seg">
                              ${o.values
                                .map((v) => {
                                  const dead = a.isUnavailable(o.name, v);
                                  return `<button type="button" class="lc-config__seg-btn${dead ? ' lc-config__seg-btn--unavailable' : ''}" data-action="addon-pick" data-key="${esc(a.key)}" data-option="${esc(o.name)}" data-value="${esc(v)}" aria-pressed="${same(a.selected[o.name], v)}" ${dead ? `disabled aria-disabled="true" title="${esc(soldOut)}"` : ''}>${esc(v)}</button>`;
                                })
                                .join('')}
                            </span>`;
                       return `<span class="lc-config__addon-option-label">${esc(isColour ? t.colourLabel || 'Colour' : o.name)}</span>${control}`;
                     })
                     .join('')}
                 </div>
                 <div class="lc-config__addon-qty">
                   <span class="lc-config__addon-option-label">${esc(t.qty || 'Qty')}</span>
                   <span class="lc-config__stepper lc-config__stepper--bordered" role="group" aria-label="${esc(t.qty || 'Quantity')}">
                     <button type="button" data-action="addon-qty" data-key="${esc(a.key)}" data-value="-1" aria-label="Decrease quantity" ${a.qty <= 1 ? 'disabled' : ''}>−</button>
                     <span class="lc-config__stepper-value">${a.qty}</span>
                     <button type="button" data-action="addon-qty" data-key="${esc(a.key)}" data-value="1" aria-label="Increase quantity" ${a.qty >= 20 ? 'disabled' : ''}>+</button>
                   </span>
                 </div>
               </div>
               <div class="lc-config__addon-confirm">
                 <span class="lc-config__addon-price">${esc(a.priceLabel)}</span>
                 <button type="button" class="lc-btn lc-btn--primary lc-btn--sm lc-config__addon-confirm-btn" data-action="addon-confirm" data-key="${esc(a.key)}" ${!a.ready || this.state.busy ? 'disabled' : ''}>${esc(t.addToSetup || 'Add to setup')}</button>
               </div>
             </div>`
          : '';

        return `<div class="lc-config__addon${a.added ? ' lc-config__addon--added' : ''}">${head}${panel}</div>`;
      })
      .join('');

    return `
      <div class="lc-config__addons-head">
        <div class="lc-config__addons-title">${esc(t.addonsTitle || 'Complete your setup')}</div>
        <p class="lc-config__addons-text">${esc(t.addonsText || 'Optional extras that work with your selection.')}</p>
      </div>
      <div class="lc-config__addon-grid">${cards}</div>`;
  }

  renderCartNote() {
    const t = this.config.text;
    const count = this.state.cartCount;
    return `
      <div class="lc-config__cart-note">
        <div class="lc-config__cart-note-title">${esc(t.readyTitle || 'Ready to go?')}</div>
        <div class="lc-config__cart-note-text">${count} ${count === 1 ? 'item' : 'items'} · ${this.money(this.state.cartTotal)}</div>
        <button type="button" class="lc-btn lc-btn--primary lc-btn--lg lc-btn--full lc-btn--square" data-action="view-cart">${esc(t.viewCart || 'View cart')}</button>
      </div>`;
  }

  // ------------------------------------------------------------------ events

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-action]') : null;
    if (!(target instanceof HTMLElement) || !this.contains(target)) return;
    const { action, value = '', key = '', option = '' } = target.dataset;

    switch (action) {
      case 'source':
        this.setState({ source: value, flock: null, colour: null, packQty: 1, edit: null, setupSignature: null });
        break;
      case 'flock':
        this.setState({ flock: Number(value), packQty: 1, edit: null });
        break;
      case 'pack-qty':
        this.setState({ packQty: Math.min(10, Math.max(1, (this.state.packQty || 1) + Number(value))) });
        break;
      case 'addon-qty': {
        const st = this.#addonState(key);
        st.qty = Math.min(20, Math.max(1, (st.qty || 1) + Number(value)));
        this.render();
        break;
      }
      case 'colour':
        this.setState({ colour: value, edit: null });
        break;
      case 'edit':
        this.setState({ edit: value });
        break;
      case 'toggle-details':
        this.setState({ detailsOpen: !this.state.detailsOpen });
        break;
      case 'add-setup':
        this.#addSetup();
        break;
      case 'addon-add':
      case 'addon-edit':
        this.#addonOpen(key);
        break;
      case 'addon-pick':
        this.#addonPick(key, option, value);
        break;
      case 'addon-confirm':
        this.#addonConfirm(key);
        break;
      case 'addon-remove':
        this.#addonRemove(key);
        break;
      case 'line-remove':
        if (key.startsWith('addon-')) this.#addonRemove(key.slice(6));
        else this.#removeSetupLine();
        break;
      case 'view-cart':
        this.#openCart();
        break;
      default:
        break;
    }
  };

  /** @param {string} key */
  #addonState(key) {
    return this.state.addons[key] ?? (this.state.addons[key] = { selected: {}, chosen: false, qty: 1, inCart: null });
  }

  /** @param {string} key */
  #addonOpen(key) {
    const view = this.addonViews().find((a) => a.key === key);
    if (!view || !view.product) return;
    const st = this.#addonState(key);
    if (!view.needsChoice) {
      this.#addonConfirm(key);
      return;
    }
    // Open with the first value of each option pre-selected (design), so the panel is
    // confirmable immediately and the shopper only changes what they want to change.
    for (const option of view.options) {
      const first = option.values.find((v) => !view.isUnavailable(option.name, v)) ?? option.values[0];
      if (!st.selected[option.name] && first) st.selected = { ...st.selected, [option.name]: first };
    }
    this.setState({ expanded: key });
  }

  /** @param {string} key @param {string} option @param {string} value */
  #addonPick(key, option, value) {
    const view = this.addonViews().find((a) => a.key === key);
    if (view?.isUnavailable(option, value)) return;
    const st = this.#addonState(key);
    st.selected = { ...st.selected, [option]: value };
    this.render();
  }

  /**
   * "Add to setup": the add-on joins the setup summary. Before the setup is in the cart nothing
   * is added yet — the setup button adds the cups and the chosen add-ons together (design). Once
   * the setup is in the cart, the add-on goes straight into the cart too, so the card's "Added"
   * always means added (a changed choice replaces the earlier cart line).
   * @param {string} key
   */
  async #addonConfirm(key) {
    const view = this.addonViews().find((a) => a.key === key);
    if (!view || !view.resolved || !view.ready) return;
    const st = this.#addonState(key);
    st.chosen = true;

    const setupInCart = Boolean(this.state.setupSignature) && Boolean(this.state.setupCupsVariantId);
    if (!setupInCart) {
      this.setState({ expanded: null, error: '' });
      return;
    }

    const previous = st.inCart;
    this.setState({ busy: true, error: '' });
    try {
      if (previous) await this.#removeVariants([previous]);
      await this.#addItems([{ id: view.resolved.id, quantity: view.qty }]);
      st.inCart = view.resolved.id;
      this.state.setupVariantIds = [...this.state.setupVariantIds.filter((id) => id !== previous), view.resolved.id];
      this.state.setupSignature = this.setup().signature;
      this.setState({ expanded: null, busy: false });
    } catch (error) {
      this.setState({ busy: false, error: error instanceof Error ? error.message : String(error) });
    }
  }

  /** Drop an add-on from the setup, and from the cart if the setup button already put it there. */
  /** @param {string} key */
  async #addonRemove(key) {
    const st = this.#addonState(key);
    const variantId = st.inCart;
    this.setState({ busy: true, error: '' });
    try {
      if (variantId) await this.#removeVariants([variantId]);
      st.inCart = null;
      st.chosen = false;
      st.selected = {};
      st.qty = 1;
      this.state.setupVariantIds = this.state.setupVariantIds.filter((id) => id !== variantId);
      // The cups are still in the cart: keep the setup button in its "added" state for the
      // remaining lines rather than offering to add the same cups again.
      if (this.state.setupSignature && this.state.setupCupsVariantId) this.state.setupSignature = this.setup().signature;
      this.setState({ expanded: null, busy: false });
    } catch (error) {
      this.setState({ busy: false, error: error instanceof Error ? error.message : String(error) });
    }
  }

  /**
   * Remove link on the cups line (design): clears the flock and colour answers and takes the cups
   * out of the cart if they were added. Chosen add-ons stay chosen, and stay in the cart.
   */
  async #removeSetupLine() {
    const cupsId = this.state.setupCupsVariantId;
    this.setState({ busy: true, error: '' });
    try {
      if (cupsId) await this.#removeVariants([cupsId]);
      this.setState({
        busy: false,
        flock: null,
        colour: null,
        packQty: 1,
        edit: null,
        detailsOpen: false,
        setupSignature: null,
        setupCupsVariantId: null,
        setupVariantIds: this.state.setupVariantIds.filter((id) => id !== cupsId),
      });
    } catch (error) {
      this.setState({ busy: false, error: error instanceof Error ? error.message : String(error) });
    }
  }

  /** Add the cups and every chosen add-on to the cart together (design: "Add setup to cart"). */
  async #addSetup() {
    const setup = this.setup();
    if (!setup.complete || !setup.purchasable) return;
    const items = setup.lines
      .filter((l) => l.variantId && l.quantity > 0)
      .map((l) => ({ key: l.key, id: /** @type {number} */ (l.variantId), quantity: l.quantity }));
    if (!items.length) return;

    this.setState({ busy: true, error: '' });
    try {
      // Re-adding a changed setup replaces its lines rather than stacking duplicates.
      if (this.state.setupVariantIds.length) await this.#removeVariants(this.state.setupVariantIds);
      await this.#addItems(items.map((i) => ({ id: i.id, quantity: i.quantity })));
      let cupsId = null;
      for (const item of items) {
        if (item.key.startsWith('addon-')) this.#addonState(item.key.slice(6)).inCart = item.id;
        else cupsId = item.id;
      }
      this.setState({ busy: false, setupSignature: setup.signature, setupVariantIds: items.map((i) => i.id), setupCupsVariantId: cupsId });
    } catch (error) {
      this.setState({ busy: false, error: error instanceof Error ? error.message : String(error) });
    }
  }

  // ------------------------------------------------------------------ cart

  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');
  }

  async #refreshCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    const cart = await response.json();
    this.state.cartCount = cart.item_count ?? 0;
    this.state.cartTotal = cart.total_price ?? 0;
    this.render();
    return cart;
  }

  /** @param {{ id: number, quantity: number }[]} items */
  async #addItems(items) {
    const deferred = CartLinesUpdateEvent.createPromise();
    const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: this.#cartSectionIds() }),
      });
      if (!response.ok) throw new Error('The cart is temporarily unavailable. Please try again.');
      const json = await response.json();

      if (json.status) {
        this.dispatchEvent(new CartErrorEvent({ error: json.message || 'Add to cart failed', code: 'INVALID' }));
        const cart = await this.#refreshCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'lc-watering-configurator', sourceId: this.id, itemCount },
        });
        throw new Error(json.description || json.message || 'Add to cart failed');
      }

      const cart = await this.#refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'lc-watering-configurator', sourceId: this.id, itemCount, sections: json.sections, didError: false },
      });
      return cart;
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  }

  /** @param {number[]} variantIds */
  async #removeVariants(variantIds) {
    for (const id of variantIds) {
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'remove',
          context: 'cart',
          lines: [{ id: String(id), quantity: 0 }],
          promise: deferred.promise,
        })
      );
      try {
        const response = await fetch(`${Theme.routes.cart_change_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: String(id), quantity: 0, sections: this.#cartSectionIds() }),
        });
        if (!response.ok) throw new Error('The cart is temporarily unavailable. Please try again.');
        const json = await response.json();
        if (json.status) throw new Error(json.description || json.message || 'Cart update failed');
        const cart = await this.#refreshCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'lc-watering-configurator', sourceId: this.id, itemCount: 0, sections: json.sections, didError: false },
        });
      } catch (error) {
        deferred.reject(error);
        throw error;
      }
    }
  }

  #openCart() {
    const actions = window.Shopify?.actions;
    if (actions?.openCart) {
      actions.openCart();
      return;
    }
    /** @type {HTMLElement & { open?: () => void } | null} */
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer?.open) drawer.open();
    else window.location.href = Theme.routes.cart_url || '/cart';
  }
}

if (!customElements.get('lc-watering-configurator')) {
  customElements.define('lc-watering-configurator', LcWateringConfigurator);
}
